// DynamicChart.jsx
import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { useDynamicChartData } from "./useChartData";

export default function DynamicChart({
  visual,
  style = { width: "100%", height: "100%" },
}) {
  const css = (name) => `var(${name})`;

  // outer container (the thing that actually changes size)
  const wrapperRef = useRef(null);
  // absolute child that ECharts mounts into
  const elRef = useRef(null);
  const chartRef = useRef(null);

  const lastInit = useRef({ theme: null, renderer: null, dpr: null });
  const lastSeriesType = useRef(null);

  const { option, isLoading, status } = useDynamicChartData(visual);
  const hasOption = !!option?.OptionJson;

  const parsedOption = useMemo(() => {
    if (!hasOption) return null;
    try {
      const raw = option.OptionJson;
      const opt =
        typeof raw === "string" ? JSON.parse(raw) : JSON.parse(JSON.stringify(raw));
      if (!opt.backgroundColor) opt.backgroundColor = "transparent";
      return opt;
    } catch {
      return { __invalid: true };
    }
  }, [hasOption, option?.OptionJson]);

  // init / re-init on renderer/theme/dpr changes
  useEffect(() => {
    if (!hasOption || !elRef.current) return;

    const theme = option?.Theme || "default";
    const renderer = (option?.Renderer || "canvas").toLowerCase();
    const dpr = option?.DevicePixelRatio || window.devicePixelRatio || 1;

    const needReinit =
      !chartRef.current ||
      lastInit.current.theme !== theme ||
      lastInit.current.renderer !== renderer ||
      lastInit.current.dpr !== dpr;

    if (needReinit) {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
      chartRef.current = echarts.init(elRef.current, theme, {
        renderer,
        devicePixelRatio: dpr,
        useDirtyRect: true,
      });
      lastInit.current = { theme, renderer, dpr };
      lastSeriesType.current = null;
    }
  }, [hasOption, option?.Theme, option?.Renderer, option?.DevicePixelRatio]);

  // apply options (no re-init)
  useEffect(() => {
    if (!hasOption || !chartRef.current) return;

    const chart = chartRef.current;
    const opt = parsedOption;

    if (opt && opt.__invalid) {
      chart.clear();
      return;
    }

    const getFirstSeriesType = (o) => {
      const s = o?.series;
      if (Array.isArray(s) && s.length && s[0]?.type) return s[0].type;
      return null;
    };

    const nextSeriesType = opt ? getFirstSeriesType(opt) : null;
    const typeChanged =
      nextSeriesType && lastSeriesType.current && nextSeriesType !== lastSeriesType.current;

    const rafId = requestAnimationFrame(() => {
      try {
        if (!opt) return;
        if (typeChanged) chart.clear();

        chart.setOption(opt, {
          // default notMerge=false, lazyUpdate=true
          notMerge: option?.NotMerge === true,
          lazyUpdate: option?.LazyUpdate !== false,
          silent: !!option?.Silent,
        });

        lastSeriesType.current = nextSeriesType;
      } catch {
        chart.clear();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [hasOption, parsedOption, option?.NotMerge, option?.LazyUpdate, option?.Silent]);

  // ---- Robust resize handling --------------------------------------------
  // Observe the *wrapper* (and the inner el as a backup), plus window + visibility.
  useEffect(() => {
    const safeResize = () => {
      const chart = chartRef.current;
      if (!chart || chart.isDisposed()) return;
      try {
        // Avoid zero-sized resizes (e.g., hidden tabs)
        const host = chart.getDom();
        const w = host?.clientWidth ?? 0;
        const h = host?.clientHeight ?? 0;
        if (w > 0 && h > 0) chart.resize();
      } catch {
        /* noop */
      }
    };

    let rafId = 0;
    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(safeResize);
    };

    const targets = [wrapperRef.current, elRef.current].filter(Boolean);
    const ro = new ResizeObserver(schedule);
    targets.forEach((t) => ro.observe(t));

    // Window resize fallback (covers cases RO misses, e.g., viewport changes)
    const onWin = schedule;
    window.addEventListener("resize", onWin);

    // When tab/page becomes visible again, force a resize
    const onVis = () => {
      if (document.visibilityState === "visible") schedule();
    };
    document.addEventListener("visibilitychange", onVis);

    // Some layouts animate width/height; run after transition ends
    const onTransitionEnd = schedule;
    targets.forEach((t) => t.addEventListener("transitionend", onTransitionEnd));

    // Fullscreen toggles
    const onFs = schedule;
    document.addEventListener("fullscreenchange", onFs);

    // Initial pass (in case we mounted with 0x0 then immediately laid out)
    schedule();

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWin);
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("fullscreenchange", onFs);
      targets.forEach((t) => t.removeEventListener("transitionend", onTransitionEnd));
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []); // attach once; refs are set after first paint

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  const showLoadingOverlay = isLoading || !hasOption || parsedOption?.__invalid;

  return (
    <div
      ref={wrapperRef}
      style={{
        ...style,                 // must resolve to a *real* size from parents
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        ref={elRef}
        style={{
          position: "absolute",
          inset: 0,              // fills wrapper
        }}
      />

      {showLoadingOverlay && (
        <div
          aria-live="polite"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            background: parsedOption?.__invalid ? "transparent" : css("--chart-loading-bg"),
            color: css("--text-primary"),
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 520 }}>
            <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: `2px solid ${css("--border-muted")}`,
                  borderTopColor: css("--accent"),
                  animation: "spin 0.9s linear infinite",
                }}
              />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
              {parsedOption?.__invalid ? "We couldn’t show this chart right now.": status || "Preparing your chart…"}
            </div>
          </div>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
          `}</style>
        </div>
      )}
    </div>
  );
}
